// Les Reels — toutes les vidéos de la communauté, plein écran, défilement
// vertical. La vidéo à l'écran se lance seule et en sourdine, comme sur
// Facebook, et se met en pause dès qu'elle sort du cadre.
//
// C'est le premier écran migré qui pilote un objet du navigateur : un
// `IntersectionObserver`. React ne sait pas « quelle vidéo est visible » — c'est
// une question de mise en page, pas d'état — donc on garde l'observateur tel
// quel et on lui confie chaque carte à son apparition.
//
// La sourdine, elle, est bien de l'état : elle vaut pour TOUTES les vidéos, et
// couper le son sur l'une doit le couper sur les suivantes. Elle est donc tenue
// par l'écran, et non par chaque carte.

import { useEffect, useMemo, useRef, useState } from "react";
import Store from "../../store.js";
import { ilYA } from "../../ui.js";
import { partager } from "../../views/partage.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone, { IconeDuo } from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import Vide from "../Vide.jsx";
import { useAbonnement, useAmens, useMembre } from "../hooks.js";

/**
 * L'observateur qui lance et met en pause.
 *
 * Il lit la sourdine dans une RÉFÉRENCE et non dans une variable d'état : son
 * rappel est installé une fois pour toute la durée de l'écran, et une valeur
 * capturée à ce moment-là resterait figée sur la sourdine du départ.
 */
function useLectureAuVol(sourdineRef) {
  const observateur = useRef(null);

  useEffect(() => {
    observateur.current = new IntersectionObserver(
      (entrees) => {
        for (const e of entrees) {
          const v = e.target.querySelector("video");
          if (!v) continue;
          if (e.isIntersecting) {
            v.muted = sourdineRef.current;
            // Un refus de lecture automatique n'est pas une erreur : certains
            // navigateurs l'interdisent tant que rien n'a été touché.
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        }
      },
      { threshold: 0.6 }
    );
    return () => observateur.current.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return observateur;
}

function Reel({ p, ctx, observateur, sourdine, onSourdine }) {
  const carte = useRef(null);
  const video = useRef(null);
  const amens = useAmens(ctx);
  const soutenu = amens.has(p.id);
  const auteur = useMembre(ctx, p.authorId, { nom: p.auteurNom, photo: p.auteurPhoto });

  useEffect(() => {
    const el = carte.current;
    const obs = observateur.current;
    obs?.observe(el);
    return () => {
      obs?.unobserve(el);
      // Une vidéo retirée de la page peut continuer à jouer son son : on la
      // range explicitement.
      video.current?.pause();
    };
  }, [observateur]);

  // `muted` posé sur la propriété et non par l'attribut : c'est la seule façon
  // fiable d'obtenir le silence sur tous les navigateurs.
  useEffect(() => {
    if (video.current) video.current.muted = sourdine;
  }, [sourdine]);

  const amen = async () => {
    const ajout = !soutenu;
    await Store.amens.toggle(p.id);
    if (ajout)
      Store.notifs.add(p.authorId, {
        type: "amen",
        deId: ctx.moi.uid,
        deNom: ctx.moi.nom,
        dePhoto: ctx.moi.photo || "",
        postId: p.id,
        apercu: (p.texte || "").slice(0, 80),
      });
  };

  return (
    <article className="reel" ref={carte}>
      <video
        ref={video}
        src={p.video}
        playsInline
        loop
        preload="metadata"
        onClick={(e) => {
          const v = e.currentTarget;
          v.paused ? v.play().catch(() => {}) : v.pause();
        }}
      />
      <div className="reel__voile" />
      <button
        className="reel__son"
        aria-label={sourdine ? "Activer le son" : "Couper le son"}
        onClick={onSourdine}
      >
        <Icone nom={sourdine ? "muet" : "son"} taille={20} />
      </button>

      <div className="reel__infos">
        <a className="reel__auteur" href={`#/profil/${p.authorId}`}>
          <Avatar membre={auteur} taille={40} />
          <span>
            <strong>
              {auteur.nom}
              <Certifie membre={auteur} taille={14} />
            </strong>
            <span className="petit">{ilYA(p.createdAt)}</span>
          </span>
        </a>
        {p.texte && (
          <p className="reel__texte">
            <TexteRiche texte={p.texte} />
          </p>
        )}
      </div>

      <div className="reel__actions">
        <button aria-label="Amen" className={soutenu ? "est-actif" : ""} onClick={amen}>
          <IconeDuo nom="amen" taille={28} />
          <b>{p.amens || 0}</b>
        </button>
        <a href={`#/publication/${p.id}`} aria-label="Commentaires">
          <Icone nom="commenter" taille={28} />
          <b>{p.commentaires || 0}</b>
        </a>
        <button aria-label="Partager" onClick={() => partager(p, ctx)}>
          <Icone nom="partager" taille={28} />
          <b>Partager</b>
        </button>
      </div>
    </article>
  );
}

/* De quoi remplir l'écran sans remonter tout le fil. */
const CIBLE = 8;
const PAGES_MAX = 6;   // six pages de vingt : cent vingt publications, pas plus

export default function Reels({ ctx }) {
  const [sourdine, setSourdine] = useState(true);
  const sourdineRef = useRef(true);
  const observateur = useLectureAuVol(sourdineRef);

  const posts = useAbonnement((cb) => Store.posts.subscribe(cb), []);

  /* ⚠️ LES REELS NE PEUVENT PAS SE CONTENTER DU FIL.
     `posts.subscribe` rend les VINGT dernières publications (`TAILLE_PAGE`).
     Tant que le réseau était jeune, les vidéos y étaient. Passé vingt
     publications, elles en sortent — et cet écran annonçait « Aucun reel » à
     quelqu'un qui en avait publié trois. Mesuré sur le compte du fondateur le
     16 août 2026 : vingt publications reçues, aucune vidéo parmi elles, alors
     que deux existaient bel et bien.

     On remonte donc le fil page par page, avec la MÊME fonction que le
     défilement infini du fil — mêmes requêtes par paquets d'auteurs, donc
     rien de neuf côté règles. `PAGES_MAX` borne la remontée : sur un réseau
     sans aucune vidéo, on ne descend pas le fil entier à chaque ouverture. */
  const [plus, setPlus] = useState([]);
  const [fini, setFini] = useState(false);
  const remonte = useRef(false);

  const tous = useMemo(() => {
    const vus = new Set();
    return [...posts, ...plus].filter((p) => !vus.has(p.id) && vus.add(p.id));
  }, [posts, plus]);

  const videos = useMemo(() => tous.filter((p) => p.video), [tous]);

  useEffect(() => {
    if (!posts.length || remonte.current) return;
    if (videos.length >= CIBLE) return;
    remonte.current = true;

    let vivant = true;
    (async () => {
      const recolte = [];
      let avant = [...tous].sort((a, b) => a.createdAt - b.createdAt)[0]?.createdAt;
      for (let page = 0; page < PAGES_MAX; page++) {
        if (!avant) break;
        let lot = [];
        try {
          lot = await Store.posts.page({ avant });
        } catch {
          break; // le serveur a refusé : on garde ce qu'on a
        }
        if (!lot.length) break; // le fil est épuisé
        recolte.push(...lot);
        avant = lot[lot.length - 1]?.createdAt;
        if (recolte.filter((x) => x.video).length + videos.length >= CIBLE) break;
      }
      if (!vivant) return;
      if (recolte.length) setPlus(recolte);
      setFini(true);
    })();
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  const basculerSon = () => {
    const suivant = !sourdineRef.current;
    sourdineRef.current = suivant; // pour l'observateur, tout de suite
    setSourdine(suivant); // pour l'affichage, au prochain rendu
  };

  /* Tant qu'on remonte le fil, on ne dit pas « aucun reel » : ce serait
     affirmer une absence qu'on n'a pas encore vérifiée. */
  if (!videos.length && !fini)
    return (
      <div className="reels">
        <Vide symbole="🎬" titre="Recherche des vidéos…" />
      </div>
    );

  if (!videos.length)
    return (
      <div className="reels">
        <Vide
          symbole="🎬"
          titre="Aucun reel pour l'instant"
          texte="Publiez une vidéo depuis le fil : elle apparaîtra ici."
        />
      </div>
    );

  return (
    <div className="reels">
      {videos.map((p) => (
        <Reel
          key={p.id}
          p={p}
          ctx={ctx}
          observateur={observateur}
          sourdine={sourdine}
          onSourdine={basculerSon}
        />
      ))}
    </div>
  );
}
